/**
 * Data-driven "this was never measured" test for the campaigns table — the
 * same rule the Insights engine applies to whole groups (D7): money moved,
 * the counter stayed at a hard zero for the entire window. A capability map
 * cannot make this call (LINE's client CAN map `cv`; this account simply
 * never sends one), so the data decides.
 *
 * Deliberately applied only at the PLATFORM row. An individual ad set with
 * zero conversions on real spend may just be a bad ad set — hiding that zero
 * would bury underperformance, the exact opposite of this table's job.
 */
export const unmeasured = (value: number, spend: number): boolean =>
  value === 0 && spend > 0;

/**
 * Below this return on spend, a reported revenue figure is not money.
 *
 * It is a default somebody set so the platform could optimise: Google on the
 * audited workspace reports B2,984 against B1,028,325 of spend — B4 a
 * conversion, 0.29%. No business runs at that return.
 *
 * The number lived as a bare `0.01` at four separate sites, each with its own
 * local predicate, while a fifth place documented itself as "the one place to
 * argue this". Raising it anywhere but everywhere would have put three panels
 * on one page in disagreement about whether the same channel reports revenue.
 */
export const MIN_PLAUSIBLE_ROAS = 0.01;

/**
 * The channel reports revenue that is plausibly money.
 *
 * Third of three states, and they are exhaustive over a channel with spend:
 * `unmeasured` never fired at all, `placeholderRevenue` fired with a number too
 * small to be real, and this is the rest.
 */
export const plausibleRevenue = (value: number, spend: number): boolean =>
  value > 0 && spend > 0 && value >= spend * MIN_PLAUSIBLE_ROAS;

/**
 * ROAS only where the value is money: null when there is no spend, no value, or a
 * value too small to be revenue. Every channel and campaign ROAS goes through
 * this, so a placeholder (XPeng Google: ฿1,321 on ฿144,063) is "—" everywhere
 * instead of a 0.01x that the chat ranked as the best channel.
 */
export const measuredRoas = (value: number, spend: number): number | null =>
  plausibleRevenue(value, spend) ? value / spend : null;

/** The counter fired, but with a number too small to be money. */
export const placeholderRevenue = (value: number, spend: number): boolean =>
  value > 0 && spend > 0 && value < spend * MIN_PLAUSIBLE_ROAS;
